import { useEffect, useRef } from 'react'
import type { Route } from '@metro/routing'
import type { MetroAnalyticsReporter } from '../../interaction/metroAnalyticsReporter'
import { recordRouteOpen } from './routeLibraryRecorder'

export interface RouteHistoryRecorderInput {
  /**
   * `true` while the rider is looking at RouteDetails. The phone modal
   * passes its `detailsVisible` flag; the desktop side panel passes the
   * same expression that gates its inline RouteDetails render.
   */
  readonly isOpen: boolean
  readonly fromSlug: string | null
  readonly toSlug: string | null
  /**
   * The selected route variant, or `null` while there is none.
   *
   * The whole route is passed rather than pre-extracted numbers so the
   * snapshot's three fields are derived in ONE place. Two call sites each
   * plucking `total_time_s` / `transfer_count` / the ride lines is exactly
   * the drift this hook exists to prevent.
   */
  readonly route: Route | null
  /** Project reporter (host injects `realMetroAnalyticsReporter`). */
  readonly reporter: MetroAnalyticsReporter
}

/**
 * Stored minutes — `max(1, round(total_time_s / 60))`, byte-for-byte the iOS
 * formula (`max(1, Int((Double(route.time) / 60.0).rounded()))`) and
 * Android's.
 *
 * The rounding matches what `RouteVariantRow` displays. The floor does NOT:
 * a sub-30-second route shows 0 min on screen and persists 1. That is
 * deliberate — the floor is a property of the STORED value, and web writing
 * 0 where the other two write 1 is precisely the kind of divergence the
 * cross-platform contract exists to prevent.
 */
function storedMinutes(route: Route): number {
  return Math.max(1, Math.round(route.total_time_s / 60))
}

/** Ride-leg line ids in travel order; walk and transfer legs carry none. */
function rideLineIds(route: Route): string[] {
  return route.legs.flatMap((leg) =>
    leg.type === 'ride' && leg.line_id !== undefined ? [leg.line_id] : [],
  )
}

/**
 * Records the RouteDetails open into the route library, exactly once per
 * (route, opening).
 *
 * One hook for both surfaces on purpose: the phone modal and the desktop
 * side panel are the same event ("the rider is looking at this route"), and
 * iOS made the same call — its sheet and its side panel both go through one
 * `_recordRouteHistory`.
 *
 * Re-renders while the detail stays open must NOT restamp the entry: on a
 * wide layout the panel re-renders on unrelated state changes, and a
 * restamped entry presents a stale computation as fresh — the hazard the
 * contract calls out for `computedAt`. The latch holds the canonical id we
 * recorded for and resets when the detail closes, so a genuine close→reopen
 * records afresh.
 */
export function useRouteHistoryRecorder(input: RouteHistoryRecorderInput): void {
  const { isOpen, fromSlug, toSlug, route, reporter } = input
  const recordedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      recordedRef.current = null
      return
    }
    if (route === null) return
    // Latch on the RAW pair, and let `recordRouteOpen` decide whether it can
    // form a key. Validating here and returning early would swallow the
    // unusable-pair case before the recorder could report it, leaving a
    // slug-less station silently undetectable in production. For a valid
    // pair this string IS the canonical id, so the happy path is unchanged;
    // for an invalid one it still latches, so a re-render does not re-report.
    const attemptId = `${fromSlug ?? ''}>>>${toSlug ?? ''}`
    if (recordedRef.current === attemptId) return
    recordedRef.current = attemptId
    recordRouteOpen(
      {
        fromSlug,
        toSlug,
        durationMinutes: storedMinutes(route),
        changesCount: route.transfer_count,
        lineIds: rideLineIds(route),
        nowIso: new Date().toISOString(),
      },
      reporter,
    )
  }, [isOpen, fromSlug, toSlug, route, reporter])
}
